import moment from 'moment';
import { escapeCsvField } from './orderCsvExport';
import { downloadCsv } from './csv';

/**
 * Build a CSV blob for the given stats response. Mirrors the layout of the
 * stats page itself — top KPIs, by-status, by-day, by-name — with separator
 * sections so the file is human-skimmable in Excel/Google Sheets.
 *
 * `meta` carries the request context (period label, partner name, etc.) that
 * isn't in the response payload but is useful to embed in the file header.
 */
export function downloadStatsCsv(stats, meta = {}) {
  if (!stats) return;
  const lines = [];

  // Header block
  lines.push(`"# OVO Stats Export"`);
  if (meta.title) lines.push(`"# ${meta.title.replace(/"/g, '""')}"`);
  if (meta.partnerName) lines.push(`"# Partner: ${meta.partnerName.replace(/"/g, '""')}"`);
  const periodLabel = stats.period ? `${stats.period.from || '…'} → ${stats.period.to || '…'}` : meta.periodLabel || '';
  if (periodLabel) lines.push(`"# Period: ${periodLabel}"`);
  lines.push(`"# Generated: ${moment().format('YYYY-MM-DD HH:mm')}"`);
  lines.push('');

  // KPIs
  lines.push('Section,Metric,Value');
  if (stats.delivery) {
    const d = stats.delivery;
    lines.push(`Delivery,Total,${esc(d.total)}`);
    lines.push(`Delivery,Completed,${esc(d.completed)}`);
    lines.push(`Delivery,Failed,${esc(d.failed)}`);
    lines.push(`Delivery,Skipped,${esc(d.skipped)}`);
    lines.push(`Delivery,Other,${esc(d.other)}`);
    lines.push(`Delivery,Completion rate %,${esc(d.completionRate)}`);
  }
  if (stats.routes) {
    const r = stats.routes;
    lines.push(`Routes,Total,${esc(r.total)}`);
    lines.push(`Routes,Avg stops/route,${esc(r.avgStopsPerRoute)}`);
    lines.push(`Routes,Total distance (km),${esc(r.totalDistanceKm)}`);
    // OVO-130 — shift duration (bike-out → bike-in) is on the admin/global
    // export only; partner exports stay client-service-only (payroll is not
    // partner-facing).
    if (r.totalShiftMins != null) {
      lines.push(`Routes,Total shift time (min),${esc(r.totalShiftMins)}`);
      lines.push(`Routes,Avg shift time/route (min),${esc(r.avgShiftMins)}`);
    }
    // OVO-130 — client service time (first pickup → last delivery). The
    // "total" is what operators previously had to compute by hand in Excel.
    lines.push(`Routes,Total service time (min),${esc(r.totalClientServiceMins ?? '')}`);
    lines.push(`Routes,Avg service time/route (min),${esc(r.avgClientServiceMins ?? r.avgDurationMins)}`);
    // Prise de service — start/close overhead (shift − client service).
    if (r.totalPriseDeServiceMins != null) {
      lines.push(`Routes,Total prise de service (min),${esc(r.totalPriseDeServiceMins)}`);
      lines.push(`Routes,Avg prise de service/route (min),${esc(r.avgPriseDeServiceMins ?? '')}`);
    }
    // OVO-239 — the two rate metrics (denominators documented on the cards).
    if (r.avgSpeedKmh != null) lines.push(`Routes,Avg speed (km/h),${esc(r.avgSpeedKmh)}`);
    if (r.stopsPerHour != null) lines.push(`Routes,Stops per hour,${esc(r.stopsPerHour)}`);
    if (r.totalCo2Kg != null) {
      lines.push(`Routes,Total CO2 (kg),${esc(r.totalCo2Kg)}`);
      lines.push(`Routes,Avg CO2 per route (kg),${esc(r.avgCo2KgPerRoute)}`);
      lines.push(`Routes,Routes with CO2 factor,${esc(r.routesWithCo2)}`);
      if (r.totalCo2SavedKg != null) {
        lines.push(`Routes,CO2 saved vs car (kg),${esc(r.totalCo2SavedKg)}`);
        lines.push(`Routes,Oil baseline (g/km),${esc(r.oilBaselineGPerKm)}`);
      }
    }
  }
  if (stats.collection) {
    const c = stats.collection;
    lines.push(`Collection,Total items,${esc(c.totalItems)}`);
    lines.push(`Collection,Avg items/route,${esc(c.avgItemsPerRoute)}`);
    lines.push(`Collection,Return drop-off rate %,${esc(c.returnDropoffRate)}`);
  }
  if (stats.time) {
    const t = stats.time;
    lines.push(`Time,On-time %,${esc(t.onTimeRate)}`);
    lines.push(`Time,On-time count,${esc(t.onTimeCount)}`);
    lines.push(`Time,Late count,${esc(t.lateCount)}`);
    lines.push(`Time,Avg time per task (min),${esc(t.avgTimePerStopMins)}`);
  }
  lines.push('');

  // By status
  if (stats.time?.ordersByStatus) {
    lines.push('# Orders by Status');
    lines.push('Status,Count');
    for (const [status, count] of Object.entries(stats.time.ordersByStatus)) {
      lines.push(`${esc(status)},${esc(count)}`);
    }
    lines.push('');
  }

  // By day of week
  if (stats.time?.ordersByDayOfWeek) {
    lines.push('# Orders by Day of Week');
    lines.push('Day,Count');
    for (const [day, count] of Object.entries(stats.time.ordersByDayOfWeek)) {
      lines.push(`${esc(day)},${esc(count)}`);
    }
    lines.push('');
  }

  // By category (collected items)
  if (stats.collection?.byCategory) {
    lines.push('# Collected Items by Category');
    lines.push('Category,Count');
    for (const [cat, count] of Object.entries(stats.collection.byCategory)) {
      lines.push(`${esc(cat)},${esc(count)}`);
    }
    lines.push('');
  }

  // Visits by client (unified pickup + delivery, deduplicated per day)
  // OVO-239 — driver + vehicle comparison rows.
  for (const [section, rows, nameKey] of [
    ['By driver', stats.byDriver, 'driverName'],
    ['By vehicle type', stats.byVehicleType, 'vehicleType'],
  ]) {
    if (!rows || rows.length === 0) continue;
    lines.push('');
    lines.push(
      `${section},Name,Routes,Orders,Completion %,Distance (km),Avg speed (km/h),Stops per hour,Client service (min)`,
    );
    rows.forEach((row) =>
      lines.push(
        [
          section,
          esc(row[nameKey]),
          esc(row.routes),
          esc(row.orders),
          esc(row.completionRate),
          esc(row.totalDistanceKm),
          esc(row.avgSpeedKmh ?? ''),
          esc(row.stopsPerHour ?? ''),
          esc(row.totalClientServiceMins ?? ''),
        ].join(','),
      ),
    );
  }

  if (stats.byClient?.rows?.length > 0) {
    lines.push('# Visits by client (1 visit = 1 day, even if both pickup + delivery)');
    lines.push('Client,Total visit days,Pickup days,Delivery days,Orders,Completed,Failed,Skipped,Completion %');
    for (const row of stats.byClient.rows) {
      lines.push(
        [
          row.name,
          row.total,
          row.pickupDays,
          row.deliveryDays,
          row.orders,
          row.completed,
          row.failed,
          row.skipped,
          row.completionRate,
        ]
          .map(esc)
          .join(','),
      );
    }
    lines.push('');
  }

  // Per-partner (global stats only)
  if (Array.isArray(stats.byPartner) && stats.byPartner.length > 0) {
    lines.push('# By Partner');
    const keys = Object.keys(stats.byPartner[0] || {});
    lines.push(keys.map(esc).join(','));
    for (const row of stats.byPartner) {
      lines.push(keys.map((k) => esc(row[k])).join(','));
    }
    lines.push('');
  }

  // By vehicle type (van vs bike) — global stats only
  if (Array.isArray(stats.byVehicleType) && stats.byVehicleType.length > 0) {
    lines.push('# By Vehicle Type');
    const keys = Object.keys(stats.byVehicleType[0] || {});
    lines.push(keys.map(esc).join(','));
    for (const row of stats.byVehicleType) {
      lines.push(keys.map((k) => esc(row[k])).join(','));
    }
    lines.push('');
  }

  // By driver — global stats only (drop the photo file key, it's UI-only noise)
  if (Array.isArray(stats.byDriver) && stats.byDriver.length > 0) {
    lines.push('# By Driver');
    const keys = Object.keys(stats.byDriver[0] || {}).filter((k) => k !== 'driverPhotoKey');
    lines.push(keys.map(esc).join(','));
    for (const row of stats.byDriver) {
      lines.push(keys.map((k) => esc(row[k])).join(','));
    }
    lines.push('');
  }

  const slug = meta.filenameSlug || 'stats';
  downloadCsv(lines, `${slug}-${moment().format('YYYY-MM-DD')}.csv`);
}

function esc(v) {
  return escapeCsvField(v);
}
